import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-hot-toast";
import { campaignApi } from "../services/api";
import {
  Campaign,
  CampaignCreate,
  CampaignFilters,
  CampaignUpdate,
} from "../types/campaign";

export const useCampaigns = (filters: CampaignFilters = {}) => {
  return useQuery(
    ["campaigns", filters],
    () => campaignApi.getCampaigns(filters),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useCampaign = (id: number) => {
  return useQuery(["campaign", id], () => campaignApi.getCampaign(id), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: CampaignCreate) => campaignApi.createCampaign(data),
    {
      onSuccess: (created: Campaign) => {
        queryClient.invalidateQueries(["campaigns"]);
        toast.success(`Campaign "${created.name}" created`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to create campaign";
        toast.error(message);
      },
    },
  );
};

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: number; data: CampaignUpdate }) =>
      campaignApi.updateCampaign(id, data),
    {
      onSuccess: (updated: Campaign) => {
        queryClient.setQueryData(["campaign", updated.id], updated);
        queryClient.invalidateQueries(["campaigns"]);
        toast.success(`Campaign "${updated.name}" updated`);
      },
      onError: (error: any) => {
        const message =
          error.response?.data?.detail || "Failed to update campaign";
        toast.error(message);
      },
    },
  );
};

export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => campaignApi.deleteCampaign(id), {
    onSuccess: (_: void, id: number) => {
      queryClient.removeQueries(["campaign", id]);
      queryClient.invalidateQueries(["campaigns"]);
      toast.success("Campaign deleted");
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || "Failed to delete campaign";
      toast.error(message);
    },
  });
};
